import { useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { EditorStarterItem } from '../../items/item-type';
import { useItems } from '../../utils/use-context';
import { useTargetItemIds } from './use-target-item-ids';

type EqualityFn<TValue> = (a: TValue, b: TValue) => boolean;

const defaultEquality = <TValue>(a: TValue, b: TValue): boolean => {
  return isEqual(a, b);
};

export type SelectionValueResult<TItem extends EditorStarterItem, TValue> = {
  value: TValue | undefined;
  isMixed: boolean;
  items: TItem[];
  targetIds: string[];
};

/**
 * 여러 아이템이 선택된 상태에서 특정 속성값이 모두 동일한지 판별해
 * Inspector 컨트롤이 표시해야 할 공통값과 혼합 여부를 제공합니다.
 *
 * @param itemId 기준이 되는 아이템 ID
 * @param selector 각 아이템에서 비교할 값을 추출하는 함수
 * @param comparator 값 비교 시 사용할 커스텀 비교 함수 (기본은 깊은 비교)
 */
export const useSelectionValues = <TItem extends EditorStarterItem, TValue>(
  itemId: string,
  selector: (item: TItem) => TValue,
  comparator?: EqualityFn<TValue>,
): SelectionValueResult<TItem, TValue> => {
  const targetIds = useTargetItemIds(itemId);
  const items = useItems(targetIds) as TItem[];

  return useMemo(() => {
    if (items.length === 0) {
      return {
        value: undefined,
        isMixed: false,
        items,
        targetIds,
      };
    }

    const firstItem = items[0]!;
    const compare: EqualityFn<TValue> = comparator ?? defaultEquality;
    const firstValue = selector(firstItem);
    // 최초 아이템에서 추출한 값과 나머지 아이템의 값을 비교해 하나라도 다르면 혼합(Mixed) 상태입니다.
    const mixed = items.some((item) => !compare(selector(item), firstValue));

    return {
      value: firstValue,
      isMixed: mixed,
      items,
      targetIds,
    };
  }, [items, selector, comparator, targetIds]);
};
